import React, { useState } from 'react';
import { useNavigate, Link } from 'react-router-dom';
import { useApp } from '../context/AppContext';

const ChevronLeft = () => (
  <svg width="20" height="20" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round">
    <polyline points="15 18 9 12 15 6"/>
  </svg>
);

const EyeIcon = ({ open }) => (
  <svg width="18" height="18" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round">
    {open
      ? <><path d="M1 12s4-8 11-8 11 8 11 8-4 8-11 8-11-8-11-8z"/><circle cx="12" cy="12" r="3"/></>
      : <><path d="M17.94 17.94A10.07 10.07 0 0 1 12 20c-7 0-11-8-11-8a18.45 18.45 0 0 1 5.06-5.94M9.9 4.24A9.12 9.12 0 0 1 12 4c7 0 11 8 11 8a18.5 18.5 0 0 1-2.16 3.19m-6.72-1.07a3 3 0 1 1-4.24-4.24"/><line x1="1" y1="1" x2="23" y2="23"/></>
    }
  </svg>
);

const STEPS = ['Account', 'Profile', 'Driver'];

export default function Register() {
  const navigate = useNavigate();
  const { register } = useApp();
  const [step, setStep] = useState(0);
  const [showPassword, setShowPassword] = useState(false);
  const [error, setError] = useState('');
  const [loading, setLoading] = useState(false);

  const [form, setForm] = useState({
    email: '', password: '', name: '', university: '', hometown: '', bio: '',
    isDriver: false, carMake: '', carModel: '', carColor: '',
  });

  const set = (k, v) => setForm(f => ({ ...f, [k]: v }));

  const validateStep = () => {
    if (step === 0) {
      if (!form.email) return 'Email is required.';
      if (!form.email.toLowerCase().endsWith('.edu')) return 'Please use a valid .edu email address.';
      if (!form.password || form.password.length < 8) return 'Password must be at least 8 characters.';
    }
    if (step === 1) {
      if (!form.name.trim()) return 'Full name is required.';
      if (!form.university.trim()) return 'University is required.';
    }
    if (step === 2 && form.isDriver) {
      if (!form.carMake.trim() || !form.carModel.trim() || !form.carColor.trim())
        return 'Please fill in all car details.';
    }
    return null;
  };

  const next = () => {
    const err = validateStep();
    if (err) { setError(err); return; }
    setError('');
    if (step < 2) { setStep(s => s + 1); return; }
    handleSubmit();
  };

  const handleSubmit = async () => {
    setLoading(true);
    const result = register({
      email: form.email, password: form.password,
      name: form.name, university: form.university,
      hometown: form.hometown, bio: form.bio,
      isDriver: form.isDriver,
      car: form.isDriver ? { make: form.carMake, model: form.carModel, color: form.carColor } : null,
    });
    setLoading(false);
    if (result.error) { setError(result.error); return; }
    navigate('/verify-email', { state: { userId: result.userId, code: result.verificationCode, email: form.email } });
  };

  return (
    <div className="page">
      {/* Header */}
      <div style={{ display: 'flex', alignItems: 'center', marginBottom: 28, paddingTop: 8 }}>
        <button
          onClick={() => step > 0 ? setStep(s => s - 1) : navigate('/')}
          style={{ background: 'none', padding: 4, color: 'var(--gray-300)', marginRight: 12 }}
        >
          <ChevronLeft />
        </button>
        <div style={{ flex: 1 }}>
          <h1 style={{ fontSize: 22, fontWeight: 800 }}>Create Account</h1>
          <p style={{ fontSize: 13, color: 'var(--gray-400)', marginTop: 2 }}>Step {step + 1} of {STEPS.length} — {STEPS[step]}</p>
        </div>
      </div>

      {/* Progress */}
      <div style={{ display: 'flex', gap: 6, marginBottom: 28 }}>
        {STEPS.map((_, i) => (
          <div key={i} style={{
            flex: 1, height: 4, borderRadius: 2,
            background: i <= step ? 'var(--accent)' : 'var(--navy-border)',
            transition: 'background 0.3s',
          }} />
        ))}
      </div>

      {error && <div className="alert alert-error">{error}</div>}

      {/* Step 0: Account */}
      {step === 0 && (
        <>
          <div className="form-group">
            <label className="form-label">University Email</label>
            <input
              className={`form-input ${error && !form.email.endsWith('.edu') ? 'error' : ''}`}
              type="email" placeholder="you@university.edu"
              value={form.email} onChange={e => set('email', e.target.value)}
            />
            <span className="form-hint">Must be a valid .edu email address</span>
          </div>
          <div className="form-group">
            <label className="form-label">Password</label>
            <div style={{ position: 'relative' }}>
              <input
                className="form-input"
                type={showPassword ? 'text' : 'password'}
                placeholder="At least 8 characters"
                value={form.password} onChange={e => set('password', e.target.value)}
                style={{ paddingRight: 44 }}
              />
              <button
                onClick={() => setShowPassword(s => !s)}
                style={{ position: 'absolute', right: 12, top: '50%', transform: 'translateY(-50%)',
                  background: 'none', color: 'var(--gray-400)' }}
              >
                <EyeIcon open={showPassword} />
              </button>
            </div>
          </div>
        </>
      )}

      {/* Step 1: Profile */}
      {step === 1 && (
        <>
          <div className="form-group">
            <label className="form-label">Full Name</label>
            <input className="form-input" type="text" placeholder="Jane Smith"
              value={form.name} onChange={e => set('name', e.target.value)} />
          </div>
          <div className="form-group">
            <label className="form-label">University</label>
            <input className="form-input" type="text" placeholder="e.g. MIT, Stanford, UCLA"
              value={form.university} onChange={e => set('university', e.target.value)} />
          </div>
          <div className="form-group">
            <label className="form-label">Hometown <span style={{ color: 'var(--gray-500)' }}>(optional)</span></label>
            <input className="form-input" type="text" placeholder="e.g. Chicago, IL"
              value={form.hometown} onChange={e => set('hometown', e.target.value)} />
          </div>
          <div className="form-group">
            <label className="form-label">Short Bio <span style={{ color: 'var(--gray-500)' }}>(optional)</span></label>
            <textarea className="form-input" placeholder="Tell riders a bit about yourself..."
              rows={3} value={form.bio} onChange={e => set('bio', e.target.value)}
              style={{ resize: 'none', lineHeight: 1.5 }} />
          </div>
        </>
      )}

      {/* Step 2: Driver setup */}
      {step === 2 && (
        <>
          <div style={{
            padding: '16px', borderRadius: 'var(--radius)',
            background: 'var(--navy-mid)', border: '1px solid var(--navy-border)',
            marginBottom: 20,
          }}>
            <p style={{ fontSize: 14, color: 'var(--gray-300)', marginBottom: 14, lineHeight: 1.5 }}>
              Do you want to offer rides as a driver? You can always update this later.
            </p>
            <div style={{ display: 'flex', gap: 10 }}>
              <button
                className={`btn ${form.isDriver ? 'btn-primary' : 'btn-ghost'}`}
                style={{ flex: 1 }}
                onClick={() => set('isDriver', true)}
              >
                Yes, I'll drive
              </button>
              <button
                className={`btn ${!form.isDriver ? 'btn-primary' : 'btn-ghost'}`}
                style={{ flex: 1 }}
                onClick={() => set('isDriver', false)}
              >
                Rider only
              </button>
            </div>
          </div>

          {form.isDriver && (
            <>
              <div className="form-group">
                <label className="form-label">Car Make</label>
                <input className="form-input" type="text" placeholder="e.g. Honda"
                  value={form.carMake} onChange={e => set('carMake', e.target.value)} />
              </div>
              <div className="form-group">
                <label className="form-label">Car Model</label>
                <input className="form-input" type="text" placeholder="e.g. Civic"
                  value={form.carModel} onChange={e => set('carModel', e.target.value)} />
              </div>
              <div className="form-group">
                <label className="form-label">Car Color</label>
                <input className="form-input" type="text" placeholder="e.g. Blue"
                  value={form.carColor} onChange={e => set('carColor', e.target.value)} />
              </div>
            </>
          )}
        </>
      )}

      <button
        className="btn btn-primary btn-full" onClick={next}
        disabled={loading}
        style={{ marginTop: 8, padding: '14px', fontSize: 15 }}
      >
        {loading ? 'Creating account...' : step < 2 ? 'Continue' : 'Create Account'}
      </button>

      {step === 0 && (
        <p style={{ textAlign: 'center', marginTop: 20, fontSize: 14, color: 'var(--gray-400)' }}>
          Already have an account?{' '}
          <Link to="/login" style={{ color: 'var(--accent)', fontWeight: 600 }}>Sign in</Link>
        </p>
      )}
    </div>
  );
}
